import config from '../data/algorithm-config.json';
import heroesData from '../data/heroes.json';
import type { Answer, Hero, MatchResult } from '../types';

type Dimension = { key: string; weight: number };
type Option = { id: Answer; centered: Record<string, number> };
type Question = { id: number; text: string; options: Array<Option & { text: string }> };
type Character = { name: string; vector: Record<string, number> };

const cfg = config as unknown as {
  question_count: number;
  dimensions: Dimension[];
  questions: Question[];
  characters: Character[];
  calibration: {
    response_mean: Record<string, number>;
    response_std: Record<string, number>;
    character_mean: Record<string, number>;
    character_std: Record<string, number>;
  };
};

export const questions = cfg.questions;
export const heroes = heroesData as Hero[];
export const heroByName = new Map(heroes.map((hero) => [hero.name, hero]));
export const heroById = new Map(heroes.map((hero) => [hero.id, hero]));

export function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export function calculateDisplayMatch(firstDistance: number, secondDistance: number) {
  const distanceFit = clamp((1.28 - firstDistance) / 0.83, 0, 1);
  const marginFit = clamp((secondDistance - firstDistance) / 0.25, 0, 1);
  return Math.round(clamp(82 + 12 * distanceFit + 4 * marginFit, 82, 98));
}

export function matchHero(answers: Answer[]): MatchResult {
  if (answers.length !== cfg.question_count) throw new Error(`需要 ${cfg.question_count} 个答案`);
  const dimensions = cfg.dimensions.map((dimension) => dimension.key);
  const weights = Object.fromEntries(cfg.dimensions.map((dimension) => [dimension.key, dimension.weight]));
  const raw = Object.fromEntries(dimensions.map((dimension) => [dimension, 0])) as Record<string, number>;

  cfg.questions.forEach((question, questionIndex) => {
    const option = question.options.find((candidate) => candidate.id === answers[questionIndex]);
    if (!option) throw new Error(`第 ${questionIndex + 1} 题答案无效`);
    dimensions.forEach((dimension) => { raw[dimension] += option.centered[dimension]; });
  });

  const userZ = Object.fromEntries(dimensions.map((dimension) => [
    dimension,
    (raw[dimension] - cfg.calibration.response_mean[dimension]) / cfg.calibration.response_std[dimension],
  ])) as Record<string, number>;
  const sumWeight = dimensions.reduce((sum, dimension) => sum + weights[dimension], 0);
  const distances: Record<string, number> = {};

  cfg.characters.forEach((character) => {
    const distance = dimensions.reduce((sum, dimension) => {
      const heroZ = (character.vector[dimension] - cfg.calibration.character_mean[dimension]) / cfg.calibration.character_std[dimension];
      return sum + weights[dimension] * (userZ[dimension] - heroZ) ** 2;
    }, 0);
    distances[character.name] = Math.sqrt(distance / sumWeight);
  });

  const rank = Object.entries(distances).sort((a, b) => a[1] - b[1]);
  const winner = heroByName.get(rank[0][0]);
  if (!winner) throw new Error(`找不到结果人物：${rank[0][0]}`);

  return {
    heroId: winner.id,
    top1: rank[0][0],
    top2: rank[1][0],
    top3: rank[2][0],
    closeMatch: rank[1][1] - rank[0][1] < 0.05,
    displayMatch: calculateDisplayMatch(rank[0][1], rank[1][1]),
    distances,
  };
}
